import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { LuLoaderCircle } from 'react-icons/lu';
import { Link, type LinkProps } from 'react-router-dom';
import { iconButtonClasses } from './iconButtonClasses';
import { clickGuard, inertAnchorProps, inertState, splitRouterProps } from './inertControl';

type AriaDisabledProp = { 'aria-disabled'?: boolean | 'true' | 'false' };

export type IconButtonSize = 'xs' | 'sm' | 'md';
/**
 * default: the surface disc with a hairline border (bars, dialog heads).
 * ghost: no surface until hover (toolbars, rows).
 * danger: ghost at rest, the danger tint on hover (Report, Block, Delete).
 * on-dark: for the dark stages; its border and fills are the --ee-on-dark-*
 * tokens, which a stage with its own raised surface sets on its scope.
 */
export type IconButtonTone = 'default' | 'ghost' | 'danger' | 'on-dark';

interface IconButtonOwnProps {
  /** The accessible name (aria-label). Required: an icon alone names nothing. */
  label: string;
  /** The glyph (decorative: hidden from assistive tech, the label names it). */
  icon: ReactNode;
  /**
   * md is the 44px touch target; sm (36px) only where a row is too tight; xs
   * draws a 28px disc for a dense row inside a popover or list, its hit area
   * still 44px.
   */
  size?: IconButtonSize;
  tone?: IconButtonTone;
  /** Shows a spinner for the icon, sets aria-busy and swallows clicks. Focus stays put. */
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}

type Shared = 'children' | 'aria-label';

export type IconButtonAsButtonProps = IconButtonOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof IconButtonOwnProps | Shared> & {
    href?: undefined;
    to?: undefined;
  };

export type IconButtonAsAnchorProps = IconButtonOwnProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof IconButtonOwnProps | Shared> & {
    href: string;
    to?: undefined;
  };

export type IconButtonAsLinkProps = IconButtonOwnProps &
  Omit<LinkProps, keyof IconButtonOwnProps | Shared> & { href?: undefined };

export type IconButtonProps =
  | IconButtonAsButtonProps
  | IconButtonAsAnchorProps
  | IconButtonAsLinkProps;

/**
 * The round icon-only control: a <button>, an <a> with `href`, or a router
 * Link with `to`. Shares Button's focus ring, hover gate, disabled opacity
 * and reduced-motion rule (IconButton.css). `label` is the accessible name;
 * pass `title` as well only where the control already showed a tooltip.
 *
 * `disabled` on a <button> is the native attribute. A link has no such
 * attribute, so a disabled one loses its href and tab stop and says
 * aria-disabled. `loading` and a caller's aria-disabled="true" keep the
 * control focusable but ignore clicks (`loading` also marks it busy). The
 * guard and the inert link are Button's, from inertControl.ts.
 */
export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(
  function IconButton(props, ref) {
    const {
      label,
      icon,
      size = 'md',
      tone = 'default',
      loading = false,
      disabled = false,
      className,
      onClick,
      ...rest
    } = props;

    const ariaDisabled = (rest as AriaDisabledProp)['aria-disabled'];
    // aria-disabled="true" refuses like disabled (#1052 review 1): the look
    // already draws it disabled, so a click must not get through.
    const { inert } = inertState({ disabled, loading, ariaDisabled });
    const content = loading ? (
      <LuLoaderCircle
        className="ee-icon-button__icon animate-spin motion-reduce:animate-none"
        aria-hidden="true"
      />
    ) : (
      <span className="ee-icon-button__icon" aria-hidden="true">
        {icon}
      </span>
    );
    // What every element below carries, built once (#1052 review 1).
    const shared = {
      'aria-label': label,
      className: iconButtonClasses({ size, tone, loading, className }),
      'aria-busy': loading || undefined,
      onClick: clickGuard(inert, onClick as ((e: MouseEvent<HTMLElement>) => void) | undefined),
    };

    if (rest.to !== undefined || typeof rest.href === 'string') {
      const { router, dom } = splitRouterProps(
        rest as Partial<LinkProps> & AnchorHTMLAttributes<HTMLAnchorElement>,
      );
      const { href, ...anchorRest } = dom;
      if (router.to !== undefined && !inert) {
        const linkProps = { ...anchorRest, ...shared, ...router, to: router.to };
        return (
          <Link {...linkProps} ref={ref as Ref<HTMLAnchorElement>}>
            {content}
          </Link>
        );
      }
      // An inert router link draws a plain <a> with none of the router props.
      const anchorProps = {
        ...anchorRest,
        ...shared,
        ...inertAnchorProps({
          disabled,
          loading,
          ariaDisabled,
          href,
          role: anchorRest.role,
          tabIndex: anchorRest.tabIndex,
        }),
      };
      return (
        <a {...anchorProps} ref={ref as Ref<HTMLAnchorElement>}>
          {content}
        </a>
      );
    }

    const { type = 'button', ...buttonRest } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
    const buttonProps = {
      ...buttonRest,
      ...shared,
      type,
      disabled,
      'aria-disabled': loading ? true : buttonRest['aria-disabled'],
    };
    return (
      <button {...buttonProps} ref={ref as Ref<HTMLButtonElement>}>
        {content}
      </button>
    );
  },
);
